import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Sanitização de logs: nunca logar PII ou dados confidenciais
    console.error('ErrorBoundary capturou uma falha não tratada:', {
      name: error.name,
      message: error.message,
    });
    this.props.onError?.(error, errorInfo);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <main
          role="alert"
          aria-live="assertive"
          className="flex min-h-screen flex-col items-center justify-center bg-navy px-4 py-12 text-center text-brand-offWhite"
        >
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-navy-dark p-8 shadow-2xl">
            <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-feedback-danger/20 text-feedback-danger">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold tracking-tight text-white sm:text-2xl">
              Algo inesperado aconteceu
            </h1>
            <p className="mt-3 text-sm text-brand-offWhite/80">
              Não se preocupe, suas respostas anteriores não foram perdidas. Tente recarregar a
              página para continuar.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <button
                type="button"
                onClick={this.handleReset}
                className="w-full rounded-xl bg-brand-orange px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-brand-orangeSecondary focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 focus:ring-offset-navy"
              >
                Recarregar página
              </button>
            </div>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}
